/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  ErrorResponse,
  Host,
  PaginatedHosts,
} from '../models/index';
import {
    ErrorResponseFromJSON,
    ErrorResponseToJSON,
    HostFromJSON,
    HostToJSON,
    PaginatedHostsFromJSON,
    PaginatedHostsToJSON,
} from '../models/index';

export interface CreateHostRequest {
    contentType: CreateHostContentTypeEnum;
    host: Host;
    accept?: CreateHostAcceptEnum;
    traceparent?: string;
    source?: boolean;
}

export interface DeleteHostByIdRequest {
    hostId: string;
    traceparent?: string;
}

export interface GetHostByIdRequest {
    hostId: string;
    accept?: GetHostByIdAcceptEnum;
    traceparent?: string;
}

export interface ListHostsRequest {
    accept?: ListHostsAcceptEnum;
    traceparent?: string;
    limit?: number;
    offset?: number;
}

/**
 * 
 */
export class HostsApi extends runtime.BaseAPI {

    /**
     * ## Description  This endpoint allows the creation of a new BOL Host.  ## Access Control - **AD Entra ID Role**: Requires the custom role `BOL.HOSTS.Create`. - **ACL Mode**: Not applicable for this route as it is administrative and does not involve Logistics Objects. 
     * Create a Host
     */
    async createHostRaw(requestParameters: CreateHostRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Host>> {
        if (requestParameters['contentType'] == null) {
            throw new runtime.RequiredError(
                'contentType',
                'Required parameter "contentType" was null or undefined when calling createHost().'
            );
        }

        if (requestParameters['host'] == null) {
            throw new runtime.RequiredError(
                'host',
                'Required parameter "host" was null or undefined when calling createHost().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['source'] != null) {
            queryParameters['source'] = requestParameters['source'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (requestParameters['contentType'] != null) {
            headerParameters['Content-Type'] = String(requestParameters['contentType']);
        }

        if (requestParameters['accept'] != null) {
            headerParameters['Accept'] = String(requestParameters['accept']);
        }

        if (requestParameters['traceparent'] != null) {
            headerParameters['traceparent'] = String(requestParameters['traceparent']);
        }

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oAuth2Azure", []);
        }

        if (this.configuration && this.configuration.apiKey) {
            headerParameters["Ocp-Apim-Subscription-Key"] = await this.configuration.apiKey("Ocp-Apim-Subscription-Key"); // APIKey authentication
        }


        let urlPath = `/hosts`;

        const response = await this.request({
            path: urlPath,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: HostToJSON(requestParameters['host']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => HostFromJSON(jsonValue));
    }

    /**
     * ## Description  This endpoint allows the creation of a new BOL Host.  ## Access Control - **AD Entra ID Role**: Requires the custom role `BOL.HOSTS.Create`. - **ACL Mode**: Not applicable for this route as it is administrative and does not involve Logistics Objects. 
     * Create a Host
     */
    async createHost(requestParameters: CreateHostRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Host | null | undefined > {
        const response = await this.createHostRaw(requestParameters, initOverrides);
        switch (response.raw.status) {
            case 201:
                return null;
            case 200:
                return await response.value();
            default:
                return await response.value();
        }
    }

    /**
     * ## Description  This endpoint allows the deletion of a specific Host identified by its `hostId`.  ## Access Control  - **AD Entra ID Role**: Requires the custom role `BOL.HOSTS.Delete`. - **ACL Mode**: Not applicable for this route as it is administrative and does not involve Logistics Objects. 
     * Delete a Host by hostId
     */
    async deleteHostByIdRaw(requestParameters: DeleteHostByIdRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['hostId'] == null) {
            throw new runtime.RequiredError(
                'hostId',
                'Required parameter "hostId" was null or undefined when calling deleteHostById().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (requestParameters['traceparent'] != null) {
            headerParameters['traceparent'] = String(requestParameters['traceparent']);
        }

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oAuth2Azure", []);
        }

        if (this.configuration && this.configuration.apiKey) {
            headerParameters["Ocp-Apim-Subscription-Key"] = await this.configuration.apiKey("Ocp-Apim-Subscription-Key"); // APIKey authentication
        }


        let urlPath = `/hosts/{hostId}`;
        urlPath = urlPath.replace(`{${"hostId"}}`, encodeURIComponent(String(requestParameters['hostId'])));

        const response = await this.request({
            path: urlPath,
            method: 'DELETE',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * ## Description  This endpoint allows the deletion of a specific Host identified by its `hostId`.  ## Access Control  - **AD Entra ID Role**: Requires the custom role `BOL.HOSTS.Delete`. - **ACL Mode**: Not applicable for this route as it is administrative and does not involve Logistics Objects. 
     * Delete a Host by hostId
     */
    async deleteHostById(requestParameters: DeleteHostByIdRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.deleteHostByIdRaw(requestParameters, initOverrides);
    }

    /**
     * ## Description  This endpoint retrieves a specific Host by its `hostId`.  ## Access Control  - **AD Entra ID Role**: Requires the custom role `BOL.HOSTS.Read`. - **ACL Mode**: Not applicable for this route as it is administrative and does not involve Logistics Objects. 
     * Get a Host by hostId
     */
    async getHostByIdRaw(requestParameters: GetHostByIdRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Host>> {
        if (requestParameters['hostId'] == null) {
            throw new runtime.RequiredError(
                'hostId',
                'Required parameter "hostId" was null or undefined when calling getHostById().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (requestParameters['accept'] != null) {
            headerParameters['Accept'] = String(requestParameters['accept']);
        }

        if (requestParameters['traceparent'] != null) {
            headerParameters['traceparent'] = String(requestParameters['traceparent']);
        }

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oAuth2Azure", []);
        }

        if (this.configuration && this.configuration.apiKey) {
            headerParameters["Ocp-Apim-Subscription-Key"] = await this.configuration.apiKey("Ocp-Apim-Subscription-Key"); // APIKey authentication
        }


        let urlPath = `/hosts/{hostId}`;
        urlPath = urlPath.replace(`{${"hostId"}}`, encodeURIComponent(String(requestParameters['hostId'])));

        const response = await this.request({
            path: urlPath,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => HostFromJSON(jsonValue));
    }

    /**
     * ## Description  This endpoint retrieves a specific Host by its `hostId`.  ## Access Control  - **AD Entra ID Role**: Requires the custom role `BOL.HOSTS.Read`. - **ACL Mode**: Not applicable for this route as it is administrative and does not involve Logistics Objects. 
     * Get a Host by hostId
     */
    async getHostById(requestParameters: GetHostByIdRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Host> {
        const response = await this.getHostByIdRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * ## Description  This endpoint retrieves a list of Hosts matching the specified criteria. The response is paginated to allow efficient handling of large datasets.  ## Access Control  - **AD Entra ID Role**: Requires the custom role `BOL.HOSTS.Read`. - **ACL Mode**: Not applicable for this route as it is administrative and does not involve Logistics Objects. 
     * List Hosts
     */
    async listHostsRaw(requestParameters: ListHostsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedHosts>> {
        const queryParameters: any = {};

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        if (requestParameters['accept'] != null) {
            headerParameters['Accept'] = String(requestParameters['accept']);
        }

        if (requestParameters['traceparent'] != null) {
            headerParameters['traceparent'] = String(requestParameters['traceparent']);
        }

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oAuth2Azure", []);
        }

        if (this.configuration && this.configuration.apiKey) {
            headerParameters["Ocp-Apim-Subscription-Key"] = await this.configuration.apiKey("Ocp-Apim-Subscription-Key"); // APIKey authentication
        }


        let urlPath = `/hosts`;

        const response = await this.request({
            path: urlPath,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedHostsFromJSON(jsonValue));
    }

    /**
     * ## Description  This endpoint retrieves a list of Hosts matching the specified criteria. The response is paginated to allow efficient handling of large datasets.  ## Access Control  - **AD Entra ID Role**: Requires the custom role `BOL.HOSTS.Read`. - **ACL Mode**: Not applicable for this route as it is administrative and does not involve Logistics Objects. 
     * List Hosts
     */
    async listHosts(requestParameters: ListHostsRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedHosts> {
        const response = await this.listHostsRaw(requestParameters, initOverrides);
        return await response.value();
    }

}

/**
 * @export
 */
export const CreateHostContentTypeEnum = {
    ApplicationJson: 'application/json'
} as const;
export type CreateHostContentTypeEnum = typeof CreateHostContentTypeEnum[keyof typeof CreateHostContentTypeEnum];
/**
 * @export
 */
export const CreateHostAcceptEnum = {
    ApplicationJson: 'application/json'
} as const;
export type CreateHostAcceptEnum = typeof CreateHostAcceptEnum[keyof typeof CreateHostAcceptEnum];
/**
 * @export
 */
export const GetHostByIdAcceptEnum = {
    ApplicationJson: 'application/json'
} as const;
export type GetHostByIdAcceptEnum = typeof GetHostByIdAcceptEnum[keyof typeof GetHostByIdAcceptEnum];
/**
 * @export
 */
export const ListHostsAcceptEnum = {
    ApplicationJson: 'application/json'
} as const;
export type ListHostsAcceptEnum = typeof ListHostsAcceptEnum[keyof typeof ListHostsAcceptEnum];
